<template>
    <pendo-multiselect
        ref="resourceSelect"
        class="resource-select"
        :class="`${field}-select`"
        :value="value"
        :options="options"
        :extra-label-width="60"
        :append-to-body="false"
        :allow-empty="false"
        :value-key="valueKey"
        :loading="loading"
        sort-by="name"
        :disabled="readOnly"
        hide-selected-options
        label-key="name"
        :placeholder="placeholder"
        :invalid="checkValidRule && !valid"
        :group-options-key="groupOptionsKey"
        :group-label-key="groupLabelKey"
        :max-menu-width="maxMenuWidth"
        :max-menu-height="320"
        @select="onOptionSelect"
        @highlighted-option-change="onHighlightedOptionChange"
        @open="highlightSelectedOption"
        @closed="onClosed"
        @filtered-options-change="onFilteredOptionsChange">
        <template #header="{ toggleMenu, model }">
            <div
                v-if="model[0]"
                class="resource-select__header">
                <div
                    :class="{
                        'pendo-multiselect__option': true,
                        'is-highlighted': hoverSelectedOption,
                    }"
                    @mouseenter="onHighlightedOptionChange(model[0])">
                    <pendo-group-option
                        v-if="model[0].group && !hideGroups"
                        :option="model[0]"
                        @click="toggleMenu" />
                    <pendo-icon-option
                        v-if="model[0].group && hideGroups"
                        :option="model[0]"
                        @click="toggleMenu" />
                    <pendo-guide-status-option
                        v-if="model[0].state && !model[0].staticSegmentId"
                        tooltip
                        :option="model[0]"
                        @click="toggleMenu" />
                    <pendo-status-option
                        v-if="model[0].staticSegmentId"
                        tooltip
                        :option="model[0]"
                        @click="toggleMenu" />
                </div>
            </div>
        </template>
        <template #noData>
            <slot name="noData" />
        </template>
        <template #selectedLabel="{ option }">
            <pendo-group-option
                v-if="option.group && !hideGroups"
                :option="option" />
            <pendo-icon-option
                v-if="option.group && hideGroups"
                :option="option" />
            <pendo-guide-status-option
                v-if="option.state && !option.staticSegmentId"
                tooltip
                :option="sanitizeOption(option)" />
            <pendo-status-option
                v-if="option.staticSegmentId"
                tooltip
                :option="option" />
        </template>
        <template #option="{ option }">
            <pendo-group-option
                v-if="option.group && !hideGroups"
                :option="option" />
            <pendo-icon-option
                v-if="option.group && hideGroups"
                :option="option" />
            <pendo-guide-status-option
                v-if="option.state && !option.staticSegmentId"
                tooltip
                :option="sanitizeOption(option)" />
            <pendo-status-option
                v-if="option.staticSegmentId"
                tooltip
                :option="option" />
        </template>
        <template #aside>
            <countable-hover-card
                v-if="showHoverCard"
                :id="highlightedOptionId"
                :type="countableType"
                for-multiselect />
        </template>
    </pendo-multiselect>
</template>

<script>
import {
    PendoMultiselect,
    PendoGroupOption,
    PendoGuideStatusOption,
    PendoIconOption,
    PendoStatusOption,
} from '@pendo/components';
import CountableHoverCard from '@/common/hover-card/countable-hover-card/countable-hover-card.vue';
import {
    maxMenuWidthForMultiselect,
    flattenOptions,
} from '@/common/hover-card/countable-hover-card/countable-hover-card.utils.js';
import { sanitizeState } from '@pendo/services/Formatters';
import { useWindowSize } from '@vueuse/core';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';
import { shouldShowAsidePanel as shouldShowAsidePanelUtil } from '@/utils/responsive-multiselect.utils';

export default {
    name: 'ResourceSelect',
    components: {
        CountableHoverCard,
        PendoMultiselect,
        PendoGuideStatusOption,
        PendoGroupOption,
        PendoIconOption,
        PendoStatusOption,
    },
    props: {
        value: {
            type: Object,
            default: null,
        },
        field: {
            type: String,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        options: {
            type: Array,
            default: () => [],
        },
        placeholder: {
            type: String,
            default: 'Select Option',
        },
        checkValidRule: {
            type: Boolean,
            default: false,
        },
        hideGroups: {
            type: Boolean,
            default: false,
        },
        grouped: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['input'],
    setup () {
        const { width: windowWidth } = useWindowSize();

        return {
            windowWidth,
        };
    },
    data () {
        return {
            highlightedOptionId: null,
            hoverSelectedOption: false,
            visibleOptions: [],
        };
    },
    computed: {
        showHoverCard () {
            const hasResults = this.visibleOptions.length > 0;
            const isAEU = this.$productLine(PRODUCT_LINES.ADOPT);

            return (
                !isAEU && !!this.countableType && !!this.highlightedOptionId && hasResults && shouldShowAsidePanelUtil(this.windowWidth)
            );
        },
        countableType () {
            return {
                pageId: 'page',
                featureId: 'feature',
                trackTypeId: 'trackEvent',
            }[this.field];
        },
        maxMenuWidth () {
            return maxMenuWidthForMultiselect(this.showHoverCard);
        },
        valid () {
            return this.value != null && this.value !== false;
        },
        valueKey () {
            return this.grouped ? 'name' : this.field;
        },
        groupOptionsKey () {
            return this.grouped ? 'resources' : '';
        },
        groupLabelKey () {
            return this.grouped ? 'name' : '';
        },
    },
    methods: {
        onOptionSelect (option) {
            this.$emit('input', { field: this.field, value: option });
        },
        onHighlightedOptionChange ($event) {
            if (!$event) {
                return;
            }
            this.highlightedOptionId = $event[this.field];
            this.hoverSelectedOption = $event[this.field] === this.value?.[this.field];
        },
        highlightSelectedOption () {
            if (this.value) {
                this.$refs.resourceSelect.pointerSet(-1);
                this.onHighlightedOptionChange(this.value);

                return;
            }

            if (this.visibleOptions.length > 0) {
                this.$refs.resourceSelect.pointerSet(0);
                this.onHighlightedOptionChange(this.visibleOptions[0]);
            }
        },
        sanitizeOption (option) {
            return sanitizeState(option);
        },
        onClosed () {
            this.highlightedOptionId = null;
            this.hoverSelectedOption = false;
        },
        onFilteredOptionsChange (options) {
            this.visibleOptions = flattenOptions(options);
        },
    },
};
</script>

<style scoped lang="less">
:deep(.pendo-multiselect__no-data) {
    max-width: 334px;
}
.resource-select__header {
    height: 40px;
    border-bottom: 1px solid var(--palette-gray-30);
    display: flex;
    align-items: center;
}
</style>
